// src/oura/screens/AeoAssessment.tsx
//
// AEO (Answer Engine Optimization) Assessment — ourteam-frontend#3065, BE
// epic ourteam-backend#3618. Staff-facing screen at /oura/assessment:
// prescreen → create → poll → report, plus a per-domain history list.
//
// The BE proxy that injects X-AEO-Secret doesn't exist yet (see
// src/services/aeo/index.ts) — in `npm run dev` every call is served from an
// in-memory DEV mock so this whole flow, including every terminal state
// (complete / partial+ENGINE_STUBBED / failed / 409 duplicate), is
// previewable end to end today.

import { useState } from "react";
import { RadarIcon, PlusIcon } from "lucide-react";
import { PageHeader } from "../primitives/PageHeader";
import { Button } from "../primitives/Button";
import { PrescreenCard, type PrescreenPrefill } from "../components/aeo/PrescreenCard";
import { AssessmentForm } from "../components/aeo/AssessmentForm";
import { AssessmentProgress } from "../components/aeo/AssessmentProgress";
import { AssessmentResults } from "../components/aeo/AssessmentResults";
import { HistoryList } from "../components/aeo/HistoryList";
import type { AeoAssessment } from "@service/aeo";
import { Navigate } from "react-router-dom";
import { OURA_ROOT } from "../nav";
import { SCAN_DRAFT_UI_ON, scanDraftMode } from "../newscan/flags";

/**
 * With the New scan flow on, this deep link is the last start path that
 * skips the reviewed 50 (JOURNEY-SPEC v1.1 §1.1 row F), so it lands on the
 * Businesses list, where New scan opens the flow. Off: today's screen.
 */
export function AeoAssessmentScreen() {
    if (SCAN_DRAFT_UI_ON && scanDraftMode() !== "absent") return <Navigate to={`${OURA_ROOT}/businesses`} replace />;
    return <AssessmentScreenBody />;
}

function AssessmentScreenBody() {
    const [prefill, setPrefill] = useState<PrescreenPrefill | null>(null);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [terminal, setTerminal] = useState<AeoAssessment | null>(null);
    const [domain, setDomain] = useState<string>("");
    const [notice, setNotice] = useState<string | null>(null);
    const [historyKey, setHistoryKey] = useState(0);

    const startNew = (p?: PrescreenPrefill | null) => {
        setPrefill(p ?? null);
        setActiveId(null);
        setTerminal(null);
        setNotice(null);
    };

    const handleCreated = (assessmentId: string, forDomain: string) => {
        setActiveId(assessmentId);
        setTerminal(null);
        setDomain(forDomain);
        setNotice(null);
        setHistoryKey((k) => k + 1);
    };

    const handleDuplicate = (assessmentId: string, forDomain: string) => {
        setActiveId(assessmentId);
        setTerminal(null);
        setDomain(forDomain);
        setNotice(`${forDomain} already has an assessment in flight — showing it instead of starting a new one.`);
    };

    const handleTerminal = (a: AeoAssessment) => {
        setTerminal(a);
        setHistoryKey((k) => k + 1);
    };

    const openFromHistory = (assessmentId: string, forDomain: string) => {
        setActiveId(assessmentId);
        setTerminal(null);
        setDomain(forDomain);
        setNotice(null);
    };

    const rerunFromHistory = (forDomain: string, businessName: string) => {
        startNew({ domain: forDomain, business_name: businessName, anchors_primary: "" });
        setDomain(forDomain);
    };

    const showingResult = activeId && terminal;
    const showingProgress = activeId && !terminal;

    return (
        <div>
            <PageHeader
                title="AEO Assessment"
                subtitle="Measure how visible a business is across AI answer engines (ChatGPT, Gemini) and Google, then audit the site behind the score."
                action={
                    (showingResult || showingProgress) ? (
                        <Button variant="outline" onClick={() => startNew(null)}>
                            <PlusIcon className="h-4 w-4" />
                            New assessment
                        </Button>
                    ) : undefined
                }
            />

            <div className="mb-4">
                <PrescreenCard onRunFull={(p) => { startNew(p); setDomain(p.domain); }} />
            </div>

            {notice && (
                <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-2.5 text-sm text-blue-800">
                    {notice}
                </div>
            )}

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    {showingProgress && activeId && (
                        <AssessmentProgress assessmentId={activeId} onTerminal={handleTerminal} />
                    )}
                    {showingResult && terminal && (
                        <AssessmentResults
                            assessment={terminal}
                            onRetry={
                                terminal.status === "failed"
                                    ? () =>
                                        startNew({
                                            // The BE omits `domain` from the assessment
                                            // payload; the screen's own state is what we
                                            // submitted, so it is the reliable fallback.
                                            domain: terminal.domain ?? domain,
                                            business_name: terminal.business_name ?? "",
                                            anchors_primary: "",
                                        })
                                    : undefined
                            }
                        />
                    )}
                    {!showingProgress && !showingResult && (
                        <AssessmentForm
                            prefill={prefill}
                            onCreated={handleCreated}
                            onDuplicate={handleDuplicate}
                        />
                    )}
                </div>
                <div className="lg:col-span-1">
                    {domain ? (
                        <HistoryList
                            domain={domain}
                            activeId={activeId}
                            onOpen={(id) => openFromHistory(id, domain)}
                            onRerun={rerunFromHistory}
                            refreshKey={historyKey}
                        />
                    ) : (
                        <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-gray-200 p-8 text-center text-sm text-gray-400">
                            <span className="inline-flex flex-col items-center gap-2">
                                <RadarIcon className="h-5 w-5 text-gray-300" />
                                History for a domain appears once you run a check or an assessment.
                            </span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
